import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from './AuthContext'
import { getCurrentUserProfile, UserProfile, Child } from '../services/userProfile'

interface UserProfileContextType {
  userProfile: UserProfile | null
  childrenData: Child[]
  activeChild: Child | null
  loading: boolean
  refreshProfile: () => Promise<void>
  getChildById: (childId: string) => Child | undefined
  setActiveChild: (child: Child | null) => void
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined)

export function useUserProfile() {
  const context = useContext(UserProfileContext)
  if (!context) {
    throw new Error('useUserProfile must be used within a UserProfileProvider')
  }
  return context
}

interface UserProfileProviderProps {
  children: React.ReactNode
}

export function UserProfileProvider({ children }: UserProfileProviderProps) {
  const { user, onboardingCompleted } = useAuth()
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeChild, setActiveChild] = useState<Child | null>(null)

  const fetchProfile = async () => {
    if (!user || !onboardingCompleted) {
      setUserProfile(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const profile = await getCurrentUserProfile()
      setUserProfile(profile)
    } catch (error) {
      //console.error('❌ UserProfileContext: Error fetching user profile:', error);
      setUserProfile(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProfile()
  }, [user, onboardingCompleted])

  // Set default active child when children data is loaded
  useEffect(() => {
    if (userProfile?.children && userProfile.children.length > 0 && !activeChild) {
      setActiveChild(userProfile.children[0])
    }
  }, [userProfile, activeChild])

  const getChildById = (childId: string): Child | undefined => {
    return userProfile?.children.find((child) => child.id === childId)
  }

  const value: UserProfileContextType = {
    userProfile,
    childrenData: userProfile?.children || [],
    activeChild,
    loading,
    refreshProfile: fetchProfile,
    getChildById,
    setActiveChild,
  }

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
}
